/* Shared localStorage access for the free tools.
 *
 * Two rules from the tools spec drive everything here:
 *   1. Keys are namespaced `exhale.<thing>.v<N>`. A schema change bumps N so a
 *      returning user with old data falls back cleanly instead of crashing.
 *   2. Every access is wrapped. Private browsing throws on setItem, some
 *      browsers throw on read, and a full quota throws mid-write. The tools
 *      must keep working with storage unavailable — just without memory.
 */

export const KEYS = {
  profile: "exhale.profile.v1",
  tipSplit: "exhale.tipsplit.v1",
};

// Reading `window.localStorage` itself can throw (blocked cookies in Safari),
// so even the availability probe is wrapped.
export function storageAvailable() {
  try {
    if (typeof window === "undefined" || !window.localStorage) return false;
    const probe = "exhale.probe";
    window.localStorage.setItem(probe, "1");
    window.localStorage.removeItem(probe);
    return true;
  } catch (e) {
    return false;
  }
}

export function readJSON(key, fallback = null) {
  try {
    const raw = window.localStorage.getItem(key);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    // Corrupt or hand-edited data shouldn't take the tool down with it.
    return parsed === null || parsed === undefined ? fallback : parsed;
  } catch (e) {
    return fallback;
  }
}

export function writeJSON(key, value) {
  try {
    window.localStorage.setItem(key, JSON.stringify(value));
    return true;
  } catch (e) {
    return false;
  }
}

// Shallow merge into an existing object key. Used for the shared business
// profile so one tool writing `businessName` never wipes another tool's fields.
export function mergeJSON(key, patch) {
  const current = readJSON(key, {});
  const next = { ...(current && typeof current === "object" ? current : {}), ...patch };
  writeJSON(key, next);
  return next;
}

export function removeKey(key) {
  try {
    window.localStorage.removeItem(key);
    return true;
  } catch (e) {
    return false;
  }
}
